import {
  AfterViewInit,
  Component,
  HostListener,
  OnDestroy,
  OnInit,
} from "@angular/core";
import { Subscription } from "rxjs";
import { BreadcrumbService } from "src/app/services/breadcrumb.service";
import { ScanFilterStateService } from "src/app/services/scan-filter-state.service";
import { ProductMasterService } from "src/app/services/masters/product-master.service";
import { ProductMasterResponseForDdl } from "src/app/models/Masters/productMaster";
import {
  InventoryDashboardKpi,
  InventoryDashboardReportItem,
  InventoryDashboardReportRequest,
  InventoryDashboardReportResponse,
  InventoryReportService,
} from "src/app/services/home/inventory-report.service";
import { apiErrorMessage } from "src/app/shared/api-error.util";

/** USP_GetInventoryDashboardReport's actual KPI result set - QR Balance /
 *  Daily Run Rate / Stock Cover / Reorder Level / Reorder Status. */
type InventoryMetricKey =
  | "QRBalance"
  | "DailyRunRate"
  | "StockCover"
  | "ReorderLevel";

interface InventoryKpiStat {
  value: number;
  changePercent: number;
  trendData: number[];
}

function emptyKpiStat(): InventoryKpiStat {
  return { value: 0, changePercent: 0, trendData: [] };
}

/** ReorderStatus's own MetricValue is a status phrase ("Reorder Required"),
 *  not a number - kept as its own shape rather than shoehorned into
 *  InventoryKpiStat's numeric `value`. It still carries its own
 *  PercentChange like every other metric, though, so that part matches
 *  InventoryKpiStat. */
interface InventoryStatusStat {
  value: string;
  changePercent: number;
  trendData: number[];
}

function emptyStatusStat(): InventoryStatusStat {
  return { value: "-", changePercent: 0, trendData: [] };
}

/** USP_GetInventoryDashboardReport's GraphData is a JSON array string
 *  ("[9478,9478,...]"), unlike GetQRLifeCycleDashboard's "{0,4,10,}" format -
 *  qr-lifecycle.service.ts's parseTrendGraph() doesn't apply here. */
function parseInventoryTrend(raw: string): number[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.map((v) => Number(v) || 0) : [];
  } catch (e) {
    return [];
  }
}

interface ProductFilterOption {
  id: number | null;
  name: string;
}

@Component({
  selector: "app-inventory-report",
  templateUrl: "./inventory-report.component.html",
  styleUrls: ["./inventory-report.component.css"],
})
export class InventoryReportComponent
  implements OnInit, AfterViewInit, OnDestroy
{
  breadcrumb = ["Dashboard", "Inventory Report"];
  isLightMode = document.body.classList.contains("light-mode");
  isLoading = false;
  loadError = "";

  user = JSON.parse(localStorage.getItem("user") || "{}");
  compId = Number(this.user.compId) || 1;
  fromDate: string | null = null;
  toDate: string | null = null;
  /** e.g. "Last 30 Days" / a custom "yyyy-mm-dd → yyyy-mm-dd" label - mirrors
   *  the shared navbar date button (header.component.html's #topDateLabel),
   *  sourced from the same ScanFilterStateService every other report reads.
   *  Shown in the page's own top-right pill (the applied filter itself). */
  datePresetLabel = "";

  /** "Compared to <prev period>" - the KPI cards' own per-card caption.
   *  Computed client-side (same length window immediately before fromDate)
   *  since InventoryDashboardReportResponse has no previousFromDate/
   *  previousToDate of its own, unlike QrLifeCycleInsights/
   *  ProductIntelligenceInsights. Distinct from datePresetLabel above -
   *  repeating "Last 30 Days" under every card just restated the filter
   *  already shown in the topbar pill. */
  get comparePeriodLabel(): string {
    if (!this.fromDate || !this.toDate) return "";
    const from = new Date(this.fromDate);
    const to = new Date(this.toDate);
    const days = Math.round((to.getTime() - from.getTime()) / 86400000) + 1;
    const prevTo = new Date(from);
    prevTo.setDate(prevTo.getDate() - 1);
    const prevFrom = new Date(prevTo);
    prevFrom.setDate(prevFrom.getDate() - (days - 1));
    const fmt = (d: Date, withYear: boolean) =>
      d.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: withYear ? "numeric" : undefined,
      });
    return `Compared to ${fmt(prevFrom, false)} - ${fmt(prevTo, true)}`;
  }

  // ===================== Inventory KPIs - populated from
  // USP_GetInventoryDashboardReport's KPI result set (see
  // InventoryReportService.getDashboard()). =====
  qrBalance: InventoryKpiStat = emptyKpiStat();
  dailyRunRate: InventoryKpiStat = emptyKpiStat();
  stockCover: InventoryKpiStat = emptyKpiStat();
  reorderLevel: InventoryKpiStat = emptyKpiStat();
  reorderStatus: InventoryStatusStat = emptyStatusStat();

  // ===================== Search ===================== Sent server-side as
  // `search` on every request (see buildRequest()) - USP_GetInventoryDashboardReport
  // does the filtering, this page just displays whatever reportData comes
  // back. No client-side re-filtering of the loaded page.
  searchTerm = "";
  private searchDebounce: any = null;
  reportData: InventoryDashboardReportItem[] = [];

  // ===================== Table (server-paginated) =====================
  rows = 5;
  rowsPerPageOptions = [5, 10, 25, 50, 100];
  currentPage = 1;
  totalRecords = 0;
  totalPages = 1;

  // ===================== Filters drawer (shared navbar Filters button) =====
  // Same "pending"/"applied" pattern as generated-qr-report.component.ts,
  // but the Product field itself is the same searchable combo the dashboard
  // uses (dashboard-new.component.html's #productSelectBtn/#productSelectPanel
  // - product-select-search + a filtered options list) instead of the plain
  // unsearchable list generated-qr-report.component.ts uses.
  filterProductOptions: ProductFilterOption[] = [];
  pendingProductFilter: ProductFilterOption | null = null;
  appliedProductFilter: ProductFilterOption | null = null;
  productId = 0;
  /** Drawer-local text typed into the Product combo's search box - filters
   *  filterProductOptions down to visibleProductOptions below. */
  productSearchTerm = "";
  isFilterDrawerOpen = false;
  /** Which of the drawer's own dropdowns is open, or none. */
  openDropdown: "product" | null = null;

  private themeChangeHandler = (): void => {
    this.isLightMode = document.body.classList.contains("light-mode");
  };

  // ===================== Shared navbar controls (header.component.html) -
  // delegated so the header's *ngIf-rendered controls only need to exist at
  // click time, same pattern as generated-qr-report.component.ts. =====
  private exportBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#exportBtn")) {
      e.stopPropagation();
      this.exportTable();
    }
  };

  private syncBtnClickHandler = (e: any): void => {
    if (e.target && e.target.closest && e.target.closest("#syncBtn")) {
      e.stopPropagation();
      this.refreshData();
    }
  };

  private filtersBtnClickHandler = (e: any): void => {
    if (
      e.target &&
      e.target.closest &&
      e.target.closest("#filtersTriggerBtn")
    ) {
      e.stopPropagation();
      this.openFilterDrawer();
    }
  };

  private dateRangeSub: Subscription | null = null;
  /** True once the initial date-range value has been applied - see
   *  ngOnInit's comment (same reasoning as generated-qr-report.component.ts). */
  private initialized = false;

  constructor(
    private breadcrumbService: BreadcrumbService,
    private inventoryReportService: InventoryReportService,
    private scanFilterState: ScanFilterStateService,
    private productMasterService: ProductMasterService,
  ) {}

  ngOnInit(): void {
    this.breadcrumbService.items = this.breadcrumb;
    window.addEventListener("sa-theme-change", this.themeChangeHandler);
    this.loadFilterProducts();

    // Cross-page product filter - ScanFilterStateService.productId/product
    // are the same plain fields dashboard-new.component.ts's commitFilters()
    // writes on Apply (ts:3729-3739) and reads back on init (ts:3315-3323).
    // Picking this up here means a product chosen on the dashboard (or any
    // other report) is already applied when this page loads, matching how
    // the shared date range already works below.
    if (this.scanFilterState.productId) {
      this.productId = this.scanFilterState.productId;
      this.appliedProductFilter = {
        id: this.scanFilterState.productId,
        name: this.scanFilterState.product || "",
      };
    }

    // dateRange$ is a BehaviorSubject, so it emits its current value
    // synchronously during this subscribe() call - `initialized` flips right
    // after, so the first emission doesn't double-fetch alongside the
    // loadDashboard() call below, while later changes (picked from the
    // shared navbar date button) still trigger a reload.
    this.dateRangeSub = this.scanFilterState.dateRange$.subscribe((range) => {
      this.fromDate = range.dateFrom;
      this.toDate = range.dateTo;
      this.datePresetLabel = range.datePreset || "";
      this.currentPage = 1;
      if (this.initialized) this.loadDashboard();
    });

    this.initialized = true;
    this.loadDashboard();
  }

  ngAfterViewInit(): void {
    document.addEventListener("click", this.exportBtnClickHandler);
    document.addEventListener("click", this.syncBtnClickHandler);
    document.addEventListener("click", this.filtersBtnClickHandler);
    this.updateHeaderFiltersBadge();
  }

  ngOnDestroy(): void {
    document.removeEventListener("click", this.exportBtnClickHandler);
    document.removeEventListener("click", this.syncBtnClickHandler);
    document.removeEventListener("click", this.filtersBtnClickHandler);
    window.removeEventListener("sa-theme-change", this.themeChangeHandler);
    if (this.dateRangeSub) this.dateRangeSub.unsubscribe();
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.breadcrumbService.items = [];
  }

  @HostListener("document:click")
  onDocumentClick(): void {
    this.openDropdown = null;
  }

  toggleDropdown(which: "product", ev: Event): void {
    ev.stopPropagation();
    this.openDropdown = this.openDropdown === which ? null : which;
  }

  /** Populates filterProductOptions from the product master's DDL endpoint -
   *  same fix as generated-qr-report.component.ts's loadFilterProducts: a
   *  page of inventory rows isn't a reliable source of "every product that
   *  could be filtered on". */
  private loadFilterProducts(): void {
    if (!this.compId) return;
    this.productMasterService
      .loadProductsForDdl({
        pageSize: -1,
        isActive: "1",
        compId: this.compId,
        searchValue: "",
      })
      .subscribe({
        next: (response: ProductMasterResponseForDdl) => {
          if (response.responseCode === "200" && response.details) {
            this.filterProductOptions = response.details
              .map((p) => ({ id: p.productId, name: p.productNm }))
              .sort((a, b) => a.name.localeCompare(b.name));
          }
        },
        error: (error) => {
          console.error("GetProductsForDdl error:", error);
        },
      });
  }

  private updateHeaderFiltersBadge(): void {
    const countEl = document.getElementById("filtersCount");
    const btn = document.getElementById("filtersTriggerBtn");
    if (!countEl || !btn) return;
    const active = this.appliedProductFilter ? 1 : 0;
    countEl.textContent = String(active);
    btn.classList.toggle("has-active", active > 0);
  }

  /** filterProductOptions narrowed by productSearchTerm - same search-as-you-
   *  type behavior as dashboard-new.component.ts's filterProducts(), just as
   *  a template-driven getter instead of that component's raw-DOM sync. */
  get visibleProductOptions(): ProductFilterOption[] {
    const query = this.productSearchTerm.trim().toLowerCase();
    if (!query) return this.filterProductOptions;
    return this.filterProductOptions.filter((p) =>
      p.name.toLowerCase().includes(query),
    );
  }

  openFilterDrawer(): void {
    this.pendingProductFilter = this.appliedProductFilter;
    this.productSearchTerm = "";
    this.isFilterDrawerOpen = true;
  }

  closeFilterDrawer(): void {
    this.isFilterDrawerOpen = false;
  }

  applyDrawerFilters(): void {
    this.appliedProductFilter = this.pendingProductFilter;
    this.productId = this.appliedProductFilter
      ? this.appliedProductFilter.id || 0
      : 0;
    // Pushed into the shared service so the dashboard (and every other
    // report) picks up this same product on its own next load/init - same
    // round-trip dashboard-new.component.ts's commitFilters() already does.
    this.scanFilterState.productId = this.productId || null;
    this.scanFilterState.product = this.appliedProductFilter
      ? this.appliedProductFilter.name
      : null;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    this.currentPage = 1;
    this.loadDashboard();
  }

  clearDrawerFilters(): void {
    this.pendingProductFilter = null;
    this.appliedProductFilter = null;
    this.productId = 0;
    this.scanFilterState.productId = null;
    this.scanFilterState.product = null;
    this.updateHeaderFiltersBadge();
    this.closeFilterDrawer();
    this.currentPage = 1;
    this.loadDashboard();
  }

  private buildRequest(): InventoryDashboardReportRequest {
    return {
      compId: this.compId,
      fromDt: this.fromDate,
      toDt: this.toDate,
      productId: this.productId || null,
      search: this.searchTerm.trim() || null,
      pageNo: this.currentPage,
      pageSize: this.rows,
    };
  }

  loadDashboard(): void {
    this.isLoading = true;
    this.loadError = "";
    this.inventoryReportService.getDashboard(this.buildRequest()).subscribe({
      next: (res: InventoryDashboardReportResponse) => this.applyResponse(res),
      error: (err) => {
        this.isLoading = false;
        this.loadError = apiErrorMessage(err);
        console.error("Inventory dashboard load failed", err);
      },
    });
  }

  private applyResponse(res: InventoryDashboardReportResponse): void {
    const byKey: { [k: string]: InventoryDashboardKpi } = {};
    (res.kpi || []).forEach((k) => (byKey[k.metricKey] = k));
    const toStat = (key: InventoryMetricKey): InventoryKpiStat => {
      const k = byKey[key];
      if (!k) return emptyKpiStat();
      return {
        value: Number(k.metricValue) || 0,
        changePercent: Number(k.percentChange) || 0,
        trendData: parseInventoryTrend(k.graphData),
      };
    };
    this.qrBalance = toStat("QRBalance");
    this.dailyRunRate = toStat("DailyRunRate");
    this.stockCover = toStat("StockCover");
    this.reorderLevel = toStat("ReorderLevel");

    const rs = byKey["ReorderStatus"];
    this.reorderStatus = rs
      ? {
          value: rs.metricValue || "-",
          changePercent: Number(rs.percentChange) || 0,
          trendData: parseInventoryTrend(rs.graphData),
        }
      : emptyStatusStat();

    this.reportData = res.data || [];
    this.totalRecords = res.totalRecords;
    this.totalPages = Math.max(1, res.totalPages);
    this.isLoading = false;
  }

  /** True when the current ReorderStatus phrase reads as an alert ("Reorder
   *  Required", "Out of Stock", ...) rather than a healthy/OK status. */
  get reorderStatusIsAlert(): boolean {
    return /reorder|required|low|out/i.test(this.reorderStatus.value);
  }

  /** Debounced so `search` (now sent server-side, see buildRequest()) isn't
   *  fired on every keystroke - resets to page 1 once it does fire, same as
   *  any other filter change. */
  onSearch(term: string): void {
    this.searchTerm = term;
    if (this.searchDebounce) clearTimeout(this.searchDebounce);
    this.searchDebounce = setTimeout(() => {
      this.currentPage = 1;
      this.loadDashboard();
    }, 350);
  }

  refreshData(): void {
    const syncBtn = document.getElementById("syncBtn");
    if (syncBtn) {
      syncBtn.classList.add("is-syncing");
      setTimeout(() => syncBtn.classList.remove("is-syncing"), 700);
    }
    this.loadDashboard();
  }

  onRowsChange(rows: number): void {
    this.rows = rows;
    this.currentPage = 1;
    this.loadDashboard();
  }

  get rangeStart(): number {
    return this.totalRecords === 0 ? 0 : (this.currentPage - 1) * this.rows + 1;
  }

  get rangeEnd(): number {
    return Math.min(this.currentPage * this.rows, this.totalRecords);
  }

  get pageNumbers(): (number | "...")[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const pages: (number | "...")[] = [1];
    if (current - 1 > 2) pages.push("...");
    for (
      let p = Math.max(2, current - 1);
      p <= Math.min(total - 1, current + 1);
      p++
    ) {
      pages.push(p);
    }
    if (current + 1 < total - 1) pages.push("...");
    if (total > 1) pages.push(total);
    return pages;
  }

  goToPage(page: number | "..."): void {
    if (page === "..." || page === this.currentPage) return;
    this.currentPage = page;
    this.loadDashboard();
  }

  prevPage(): void {
    if (this.currentPage > 1) {
      this.currentPage--;
      this.loadDashboard();
    }
  }

  nextPage(): void {
    if (this.currentPage < this.totalPages) {
      this.currentPage++;
      this.loadDashboard();
    }
  }

  /** The API returns a StatusColor hex per row (e.g. "#6c757d" for Out of
   *  Stock) precisely so the pill doesn't need a fixed CSS class per status
   *  string - status text varies more than the old hardcoded In Stock/Low
   *  Stock/Reorder Now set (e.g. "Out of Stock"), so this reads that color
   *  straight off the row instead of guessing a class name from the text. */
  statusPillStyle(color: string): { color: string; background: string } {
    const c = color || "#6c757d";
    return { color: c, background: c + "22" };
  }

  changeClass(pct: number): string {
    if (pct > 0) return "inv-up";
    if (pct < 0) return "inv-bad";
    return "inv-warn";
  }

  changeSign(pct: number): string {
    return pct > 0 ? "+" : "";
  }

  /** "9.4K" / "1.2L" / "3.5Cr" style - Indian numbering (lakh/crore), same
   *  rounding rules as product-intelligence / generated-qr-report's
   *  compact(), reused here for the KPI card values. */
  compact(n: number): string {
    if (n >= 1_00_00_000)
      return (n / 1_00_00_000).toFixed(2).replace(/\.00$/, "") + "Cr";
    if (n >= 1_00_000)
      return (n / 1_00_000).toFixed(2).replace(/\.00$/, "") + "L";
    if (n >= 1_000) return (n / 1_000).toFixed(2).replace(/\.00$/, "") + "K";
    return String(n);
  }

  // ===================== KPI card sparklines - built from each metric's own
  // GraphData trend series, same algorithm as
  // generated-qr-report.component.ts's buildTrendPaths(), generalized to
  // take whichever series/fallback the caller needs. The fallback curves are
  // the same decorative paths this page shipped with before real trend data
  // existed. =====
  private static readonly TREND_FALLBACKS = {
    blue: "M0,70 C30,64 45,72 70,66 C95,60 110,68 140,58 C170,48 185,58 210,44 C235,30 250,38 300,12",
    green:
      "M0,66 C30,70 45,60 70,64 C95,68 110,56 140,60 C170,64 185,50 210,52 C235,54 250,42 300,20",
    purple:
      "M0,64 C30,60 45,66 70,60 C95,54 110,62 140,52 C170,42 185,50 210,40 C235,30 250,38 300,16",
    orange:
      "M0,50 C30,52 45,48 70,50 C95,52 110,48 140,50 C170,52 185,48 210,50 C235,52 250,48 300,50",
    red: "M0,60 C30,58 45,62 70,58 C95,54 110,64 140,60 C170,56 185,68 210,64 C235,60 250,72 300,70",
  };

  private buildTrendPaths(
    data: number[],
    fallbackLine: string,
  ): { line: string; area: string } {
    if (data.length < 2) {
      return { line: fallbackLine, area: `${fallbackLine} L300,90 L0,90 Z` };
    }

    const width = 300;
    const height = 90;
    // Bigger than this method's usual 10 (see generated-qr-report
    // .component.ts's identical helper) - the KPI cards above also grew a
    // "Compared to ..." caption line, so the chart needs to start lower in
    // its own box to keep clear of that text instead of just the card's
    // shrunk visible spark region (see .inv-kpi-spark's own comment).
    const padTop = 22;
    const padBottom = 20;
    const usableHeight = height - padTop - padBottom;
    const max = Math.max(...data);
    const min = Math.min(...data);
    const range = max - min || 1;
    const baselinePad = range * 0.1;
    const paddedRange = range + baselinePad;
    const stepX = width / (data.length - 1);

    const points = data.map((v, i) => ({
      x: i * stepX,
      y:
        padTop +
        usableHeight -
        ((v - min + baselinePad) / paddedRange) * usableHeight,
    }));

    const line = points
      .map(
        (p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`,
      )
      .join(" ");
    const last = points[points.length - 1];
    const first = points[0];
    const area = `${line} L${last.x.toFixed(1)},${height} L${first.x.toFixed(1)},${height} Z`;

    return { line, area };
  }

  get qrBalanceTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.qrBalance.trendData,
      InventoryReportComponent.TREND_FALLBACKS.blue,
    );
  }
  get dailyRunRateTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.dailyRunRate.trendData,
      InventoryReportComponent.TREND_FALLBACKS.green,
    );
  }
  get stockCoverTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.stockCover.trendData,
      InventoryReportComponent.TREND_FALLBACKS.purple,
    );
  }
  get reorderLevelTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.reorderLevel.trendData,
      InventoryReportComponent.TREND_FALLBACKS.orange,
    );
  }
  get reorderStatusTrend(): { line: string; area: string } {
    return this.buildTrendPaths(
      this.reorderStatus.trendData,
      InventoryReportComponent.TREND_FALLBACKS.red,
    );
  }

  /** The on-screen table only ever holds one server-paginated page - export
   *  re-fetches the same filtered request (product/date/search) with
   *  pageSize: -1 so the CSV covers every matching record, not just
   *  whatever page happens to be loaded. Same -1-means-all convention
   *  ProductMasterService.loadProductsForDdl and
   *  generated-qr-report.component.ts's own exportTable() already use. */
  exportTable(): void {
    const request: InventoryDashboardReportRequest = {
      ...this.buildRequest(),
      pageNo: 1,
      pageSize: -1,
    };
    this.inventoryReportService.getDashboard(request).subscribe({
      next: (res: InventoryDashboardReportResponse) =>
        this.downloadCsv(res.data || []),
      error: (err) => {
        console.error("Export failed to load full report data", err);
        this.downloadCsv(this.reportData);
      },
    });
  }

  private downloadCsv(data: InventoryDashboardReportItem[]): void {
    const headers = [
      "Product",
      "SKU",
      "QR Balance",
      "Daily Run Rate",
      "Avg Daily Consumption",
      "Reorder Level",
      "Stock Cover",
    ];
    const rows = data.map((p) => [
      p.productName || "",
      p.sku || "",
      String(p.qrBalance),
      `${p.dailyRunRate}%`,
      String(p.avgDailyConsumption),
      String(p.reorderLevel),
      String(p.stockCover),
    ]);
    const escapeCsv = (val: string) => `"${(val || "").replace(/"/g, '""')}"`;
    const csv = [headers, ...rows]
      .map((cols) => cols.map(escapeCsv).join(","))
      .join("\n");
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = "inventory-report.csv";
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}
